import { Injectable, Injector } from "@angular/core";
import { CareerValuation, CareerValuationBreezeModel } from "@common/ADAPT.Common.Model/organisation/career-valuation";
import { CareerValuationCategoryValue, CareerValuationCategoryValueBreezeModel } from "@common/ADAPT.Common.Model/organisation/career-valuation-category-value";
import { Connection } from "@common/ADAPT.Common.Model/organisation/connection";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { CulturalExclusion } from "@common/ADAPT.Common.Model/organisation/cultural-exclusion";
import { CulturalRelationship } from "@common/ADAPT.Common.Model/organisation/cultural-relationship";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { BreezeService } from "@common/lib/data/breeze.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { CulturalActivityService } from "@org-common/lib/cultural-activity/cultural-activity.service";
import { CulturalLeadershipAccessLevels } from "@org-common/lib/cultural-leadership/cultural-leadership-access-levels.enum";
import { CulturalLeadershipFrameworkAuthService } from "@org-common/lib/cultural-leadership/cultural-leadership-framework-auth.service";
import { ICulturalLeadershipPersonalAnalysisData } from "app/features/culture/cultural-leadership/cultural-leadership-personal-analysis-data.interface";
import moment from "moment";
import { lastValueFrom, merge } from "rxjs";
import { CulturalLeadershipAnalysisDataBaseService } from "../../culture/cultural-leadership/cultural-leadership-analysis-data-base.service";
import { CareerValuationAnalysisFilter, ICareerValuationAnalysisFilter } from "./career-valuation-analysis-filter";
import { CareerValuationDataService } from "./career-valuation-data.service";

@Injectable({
    providedIn: "root",
})
@Autobind
export class CareerValuationAnalysisDataService extends CulturalLeadershipAnalysisDataBaseService {
    private readonly partialCareerValuationCache = new Map<string, Promise<CareerValuation[]>>();
    private readonly partialCareerValuationValueCache = new Map<string, Promise<CareerValuationCategoryValue[]>>();

    protected primeModels = [
        CareerValuationBreezeModel,
        CareerValuationCategoryValueBreezeModel,
    ];

    public constructor(
        injector: Injector,
        private careerValuationDataService: CareerValuationDataService,
        private culturalLeadershipFrameworkAuthService: CulturalLeadershipFrameworkAuthService,
        private culturalActivityService: CulturalActivityService,
        breezeService: BreezeService,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super(injector);

        merge(
            breezeService.dataCacheCleared$,
            rxjsBreezeService.entityTypeChangedInSave(CareerValuation),
            rxjsBreezeService.entityTypeChangedInSave(CareerValuationCategoryValue),
        )
            .subscribe(() => this.clearPartialDataCache());
    }

    public async promiseToGetCareerValuationAnalysisData(
        initialFilter?: ICareerValuationAnalysisFilter,
        prefetchAllData = false,
        includeExcludedPeople = false,
    ) {
        const queryOptions = {
            namedParams: {
                latestOnly: true,
            },
        };

        const filter = initialFilter ? initialFilter : CareerValuationAnalysisFilter.getDefaultFilterParameters();
        const date = filter.date ? filter.date : new Date(); // data to filter/limit all records is now or the passed in date

        try {
            const accessLevel = await this.careerValuationDataService.promiseToCheckCareerValuationAccessLevel();
            const hasQuantitativeAccess = accessLevel === CulturalLeadershipAccessLevels.QuantitativeAccess || accessLevel === CulturalLeadershipAccessLevels.FullAccess;
            const isCulturalLeader = await this.culturalLeadershipFrameworkAuthService.currentPersonIsCulturalLeader();
            const [connections, culturalRelationships, culturalExclusions] = await this.getPrimeData(queryOptions, filter, accessLevel, prefetchAllData);
            const careerValuations = await this.getCareerValuationData(queryOptions, filter, hasQuantitativeAccess);
            await this.getCareerValuationValuesData(queryOptions, filter, hasQuantitativeAccess, prefetchAllData);
            careerValuations.forEach((x) => x.categoryValues.sort((a, b) => a.category.ordinal - b.category.ordinal));
            const culturalActivities = hasQuantitativeAccess || isCulturalLeader
                ? await lastValueFrom(this.culturalActivityService.getCulturalActivitiesForActivePeople([CulturalActivityType.CareerValuation]))
                : [];
            const culturalData = this.convertCareerValuationDataToAnalysisData(careerValuations, date, culturalActivities, culturalExclusions);
            const processedCareerValuations = this.processCareerValuationAnalysisData(culturalData, connections, hasQuantitativeAccess, date, filter, culturalRelationships, culturalExclusions, culturalActivities);
            return includeExcludedPeople
                ? processedCareerValuations
                : processedCareerValuations.filter((data) => !data.personIsExcluded);
        } catch (error) {
            this.log.warn(error as string);
            return Promise.reject(error);
        }
    }

    private async getCareerValuationData(queryOptions: any, filter: ICareerValuationAnalysisFilter, hasQuantitativeAccess: boolean) {
        let key = "latestCareerValuationsByDate";

        if (!hasQuantitativeAccess) {
            queryOptions.namedParams.activeOnly = true;
        }

        if (filter.date) {
            queryOptions.namedParams.date = filter.date;
            key += filter.date.toDateString();
        } else {
            key += "Today";
        }

        if (queryOptions.isPartial) {
            return this.getCachedPartialData(this.partialCareerValuationCache, key, () => lastValueFrom(
                this.commonDataService.getWithOptions(CareerValuationBreezeModel, key, {
                    ...queryOptions,
                    namedParams: { ...queryOptions.namedParams },
                    forceRemote: true,
                })));
        }

        return lastValueFrom(this.commonDataService.getWithOptions(CareerValuationBreezeModel, key, queryOptions));
    }

    private async getCareerValuationValuesData(queryOptions: any, filter: ICareerValuationAnalysisFilter, hasQuantitativeAccess: boolean, prefetchAllData: boolean) {
        if (!prefetchAllData && !filter.showTrendOutwardsOnly) {
            let key = "latestCareerValuationValuesByDate";

            if (!hasQuantitativeAccess) {
                queryOptions.namedParams.activeOnly = true;
            }

            if (filter.date) {
                queryOptions.namedParams.date = filter.date;
                key += filter.date.toDateString();
            } else {
                key += "Today";
            }

            if (queryOptions.isPartial) {
                await this.getCachedPartialData(this.partialCareerValuationValueCache, key, () => lastValueFrom(
                    this.commonDataService.getWithOptions(CareerValuationCategoryValueBreezeModel, key, {
                        ...queryOptions,
                        namedParams: { ...queryOptions.namedParams },
                        forceRemote: true,
                    })));
            } else {
                await lastValueFrom(this.commonDataService.getWithOptions(CareerValuationCategoryValueBreezeModel, key, queryOptions));
            }
        }
    }

    private getCachedPartialData<T>(cache: Map<string, Promise<T[]>>, key: string, getData: () => Promise<T[]>) {
        const cachedData = cache.get(key);
        if (cachedData) {
            return cachedData;
        }

        const request = getData().catch((error) => {
            cache.delete(key);
            return Promise.reject(error);
        });
        cache.set(key, request);
        return request;
    }

    private clearPartialDataCache() {
        this.partialCareerValuationCache.clear();
        this.partialCareerValuationValueCache.clear();
    }

    private convertCareerValuationDataToAnalysisData(careerValuations: CareerValuation[], date: Date, culturalActivities: CulturalActivity[], culturalExclusions: CulturalExclusion[]) {
        const excludedPersonIds = new Set(culturalExclusions.map((ce) => ce.personId));
        return careerValuations.map((cv) => this.mapCareerValuation(cv, date, culturalActivities.filter((event) => event.person1Id === cv.personId), excludedPersonIds.has(cv.personId)));
    }

    private mapCareerValuation(careerValuation: CareerValuation, date: Date, culturalActivities: CulturalActivity[], personIsExcluded = false) {
        const catchupData = this.culturalLeadershipFrameworkService.getIndividualCohortMemberStatus(culturalActivities, undefined, undefined, careerValuation, undefined);

        const data = {
            person: careerValuation.person,
            personIsExcluded,
            careerValuation,
            latestCatchup: careerValuation.creationDate,
            nextCatchup: catchupData.nextCVT,
        } as ICulturalLeadershipPersonalAnalysisData;

        data.employeeConnections = data.person
            ? data.person.connections.filter((connection: Connection) => connection.isEmployeeConnection())
            : [];
        data.catchupStatus = this.culturalLeadershipFrameworkService.getCatchupStatusFromDate(date, data.nextCatchup);

        return data;
    }

    private processCareerValuationAnalysisData(culturalData: ICulturalLeadershipPersonalAnalysisData[], connections: Connection[], hasQuantitativeAccess: boolean, date: Date, filter: ICareerValuationAnalysisFilter, culturalRelationships: CulturalRelationship[], culturalExclusions: CulturalExclusion[], culturalActivities: CulturalActivity[]) {
        culturalData = this.filterByCulturalMeasurement(culturalData, connections, hasQuantitativeAccess, date, filter, culturalRelationships, culturalExclusions, culturalActivities);

        if (filter.showTrendOutwardsOnly) {
            culturalData = culturalData.filter((data) => this.filterByBadTrend(data, date));
        }

        if (filter.maximumActualPercentage && filter.minimumIdealValue) {
            culturalData = culturalData.filter((data) => this.filterByDifferential(data, filter));
        }

        return culturalData;
    }


    private filterByBadTrend(analysisData: ICulturalLeadershipPersonalAnalysisData, date: Date) {
        if (!analysisData.careerValuation || !analysisData.person) {
            return false;
        }

        // not enough data to work this out
        if (analysisData.person.careerValuations.length < 3) {
            return false;
        }

        const valueCareerValuations = analysisData.person.careerValuations
            .filter((cv: CareerValuation) => moment(cv.creationDate).isBefore(date))
            .sort((a: CareerValuation, b: CareerValuation) => moment(a.creationDate) < moment(b.creationDate) ? 1 : -1);

        // not enough data to work this out
        if (valueCareerValuations.length < 3) {
            return false;
        }

        return (valueCareerValuations[2].extensions.actualTotal - valueCareerValuations[0].extensions.actualTotal) > 15;
    }

    private filterByDifferential(analysisData: ICulturalLeadershipPersonalAnalysisData, filter: ICareerValuationAnalysisFilter) {
        if (!analysisData.careerValuation) {
            return false;
        }

        return analysisData.careerValuation.categoryValues.some((cv) => (cv.idealValue >= filter.minimumIdealValue! && ((cv.actualValue / cv.idealValue * 100) < filter.maximumActualPercentage!)));
    }
}
